import React, { useState } from "react";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import { CALENDAR_SYNC_KEYS, CALENDAR_SYNC_TONE, INTERACTION_KEYS, INTERACTION_TYPES } from "../contract";
import CrmPopover from "../ui/CrmPopover";
import CrmButton from "../ui/CrmButton";
import CrmInput from "../ui/CrmInput";
import StatusBadge from "../ui/StatusBadge";
import { cn } from "../../../lib/utils";
import { FOCUS_RING } from "../ui/classes";
import { CRM } from "../../../constants/testIds/crm";

const SUMMARY_MAX = 200;
const todayKey = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** `date` + `HH:MM` in the browser's own local time → a real instant (mirrors
 * BookingCalendar's `new Date(...).toISOString()` convention — there is no server-side
 * "practice timezone" input for a free-typed time, unlike the offered-slots booking
 * flow). Returns `null` on an incomplete or unparsable pair. */
const toInstant = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const d = new Date(`${dateStr}T${timeStr}`);
  return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * "Log interaction" (spec): Call | Meeting | Note, a date (default today), one line,
 * "Save to timeline". A Meeting additionally takes a start and end time
 * (contract C1.06 addendum `POST …/interactions {start_at, end_at}`) and, once saved,
 * shows whether it synced to the practice's Google Calendar (`calendar.status`,
 * modules/crm/calendar.py) — `null` on any other type, so only a meeting ever shows
 * this confirmation step; a call/note still saves and closes immediately.
 */
export default function LogInteractionPopover({ clientId, onLogged }) {
  const t = useT("crm");
  const tc = useT("common");
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("call");
  const [date, setDate] = useState(todayKey);
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");
  const [summary, setSummary] = useState("");
  const [busy, setBusy] = useState(false);
  const [calendarResult, setCalendarResult] = useState(null);

  const isMeeting = type === "meeting";
  const start = isMeeting ? toInstant(date, startTime) : null;
  const end = isMeeting ? toInstant(date, endTime) : null;
  const timesInvalid = isMeeting && startTime && endTime && start && end && end <= start;
  const canSave = summary.trim() && !busy && (!isMeeting || (startTime && endTime && !timesInvalid));

  const reset = () => {
    setType("call");
    setDate(todayKey());
    setStartTime("");
    setEndTime("");
    setSummary("");
    setCalendarResult(null);
  };

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const payload = { type, occurred_on: date, summary: summary.trim() };
      if (isMeeting) {
        payload.start_at = start.toISOString();
        payload.end_at = end.toISOString();
      }
      const saved = await crmApi.logInteraction(clientId, payload);
      toast.success(t("log.saved"));
      onLogged?.();
      if (isMeeting && saved?.calendar) {
        // Hold the popover open on a confirmation step so the practitioner sees whether
        // the meeting actually reached their calendar (never silent — CR-B3c-2).
        setCalendarResult(saved.calendar);
      } else {
        setOpen(false);
        reset();
      }
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc));
    } finally {
      setBusy(false);
    }
  };

  const done = () => {
    setOpen(false);
    reset();
  };

  return (
    <CrmPopover open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }} testId="crm-client-log-popover"
      trigger={<CrmButton variant="outline" data-testid={CRM.clientLog}>{t("actions.log")}</CrmButton>}>
      {calendarResult ? (
        <div data-testid={CRM.clientLogCalendarStatus}>
          <p className="mb-3 flex items-center gap-2 font-display text-xl">
            <CheckCircle2 size={20} className="text-eh-sage-deep" aria-hidden="true" />
            {t("log.saved")}
          </p>
          <StatusBadge tone={CALENDAR_SYNC_TONE[calendarResult.status] || "neutral"}>
            {t(CALENDAR_SYNC_KEYS[calendarResult.status] || "log.calendarNoConnection")}
          </StatusBadge>
          <CrmButton className="mt-4 w-full justify-center" onClick={done} data-testid={CRM.clientLogDone}>
            {t("log.done")}
          </CrmButton>
        </div>
      ) : (
        <>
          <p className="mb-3 font-display text-xl">{t("actions.log")}</p>
          <div role="radiogroup" aria-label={t("log.type")} className="flex overflow-hidden rounded-full border border-eh-border-soft">
            {INTERACTION_TYPES.map((k, i) => (
              <button key={k} type="button" role="radio" aria-checked={type === k} onClick={() => setType(k)} data-testid={CRM.clientLogType(k)}
                className={cn("flex-1 px-3 py-2 text-sm", i > 0 && "border-l border-eh-border-soft", type === k ? "bg-eh-ink text-eh-bg" : "text-eh-ink hover:bg-eh-bg", FOCUS_RING)}>
                {t(INTERACTION_KEYS[k])}
              </button>
            ))}
          </div>
          <label className="mt-3 block">
            <span className="sr-only">{t("log.date")}</span>
            <CrmInput type="date" value={date} max={todayKey()} onChange={(e) => setDate(e.target.value)} data-testid={CRM.clientLogDate} />
          </label>
          {isMeeting && (
            <div className="mt-3 grid grid-cols-2 gap-2">
              <label className="block">
                <span className="sr-only">{t("log.startTime")}</span>
                <CrmInput type="time" value={startTime} placeholder={t("log.startTime")} invalid={!!timesInvalid}
                  onChange={(e) => setStartTime(e.target.value)} data-testid={CRM.clientLogStartTime} />
              </label>
              <label className="block">
                <span className="sr-only">{t("log.endTime")}</span>
                <CrmInput type="time" value={endTime} placeholder={t("log.endTime")} invalid={!!timesInvalid}
                  onChange={(e) => setEndTime(e.target.value)} data-testid={CRM.clientLogEndTime} />
              </label>
              {timesInvalid && (
                <p role="alert" className="col-span-2 text-xs text-red-700" data-testid={CRM.clientLogTimeError}>
                  {t("log.timeInvalid")}
                </p>
              )}
            </div>
          )}
          <label className="mt-3 block">
            <span className="sr-only">{t("log.summary")}</span>
            <CrmInput value={summary} maxLength={SUMMARY_MAX} onChange={(e) => setSummary(e.target.value)} placeholder={t("log.placeholder")}
              onKeyDown={(e) => { if (e.key === "Enter" && !isMeeting) { e.preventDefault(); save(); } }} data-testid={CRM.clientLogSummary} />
          </label>
          <CrmButton className="mt-3" disabled={!canSave} onClick={save} data-testid={CRM.clientLogSave}>{t("log.save")}</CrmButton>
        </>
      )}
    </CrmPopover>
  );
}
